/**
 * live-news-types.ts
 * Shared types for the Live News component and its stream data scripts.
 */

export interface NewsSource {
    name: string
    /** 
     * Direct URL: 
     * - YouTube: last-known watch?v=... URL (used as Tier-2 fallback)
     * - HLS: direct .m3u8 playlist URL
     */
    url: string
    type: "youtube" | "hls" | "rumble" | "iframe"
    /** YouTube Channel ID — enables auto-discovery of current live stream via backend proxy */
    channelId?: string
    /** Region tag — used to merge sources into the correct NewsCategory */
    region: string
    /** Optional fallback if primary URL fails */
    fallbackUrl?: string
    /** Optional fallback type */
    fallbackType?: "youtube" | "hls" | "rumble" | "iframe"
    /** Favicon / logo URL shown in the sidebar channel list */
    icon?: string
    /** EPG TVG ID for schedule matching */
    epgId?: string
}

export interface NewsCategory {
    region: string
    sources: NewsSource[]
}

/**
 * Merges an array of flat NewsSource records into grouped NewsCategory arrays.
 * Sources belonging to the same region are collected into one category entry.
 * The order of regions follows the first occurrence order in the input array.
 */
export function buildNewsData(sources: NewsSource[]): NewsCategory[] {
    const categoryMap = new Map<string, NewsSource[]>()
    for (const source of sources) {
        if (!categoryMap.has(source.region)) {
            categoryMap.set(source.region, [])
        }
        categoryMap.get(source.region)!.push(source)
    }
    return Array.from(categoryMap.entries()).map(([region, srcs]) => ({ region, sources: srcs }))
}

/**
 * Defines the canonical region order for display.
 * Regions not in this list will appear after, in insertion order.
 */
export const REGION_ORDER = [
    "US",
    "Canada",
    "Business",
    "UK",
    "Europe",
    "Middle East",
    "India",
    "Singapore",
    "China",
    "Taiwan",
    "South Korea",
    "Japan",
    "Australia",
]
